// ============================================================================
// Project debriefs — the conversation after a project (project-debriefs brief)
// ============================================================================
// Distinct from post-event feedback: that rates ONE supplier's performance on
// ONE award. A debrief covers the whole project — what happened, what we
// learned, and what somebody now has to do about it.
//
// THE PROJECT IS THE FS NUMBER (Rob, 2026-08-12). There is no projects table;
// briefs carry fusion_project_number as free text and a debrief keys off that
// string. Everything here normalises it the same way, because "FS9879 " and
// "fs9879" being different projects is the failure mode that decision buys.
//
// The shape is topics → notes → an optional action per note. An action
// REFERENCES its note rather than copying the text (§3), so editing the note
// updates the actions list too and the two can never say different things.
// ============================================================================

// Normalising in ONE place: a project ref is trimmed and compared
// case-insensitively, everywhere, or the same project splits in two.
export const normaliseProjectRef = (ref) => String(ref ?? '').trim();
export const projectRefKey = (ref) => normaliseProjectRef(ref).toLowerCase();
export const sameProject = (a, b) => !!projectRefKey(a) && projectRefKey(a) === projectRefKey(b);

// §2 templates — a starting structure, one click, all removable. They exist so
// a debrief doesn't start at a blank page, not to dictate the conversation.
export const TOPIC_TEMPLATES = [
  { key: 'kit',       title: 'Kit & build' },
  { key: 'signoff',   title: 'Sign-off & rehearsal' },
  { key: 'logistics', title: 'Logistics' },
  { key: 'crew',      title: 'Crew & training' },
  { key: 'suppliers', title: 'Suppliers' },
  { key: 'aob',       title: 'AOB' },
];

export const ACTION_STATUSES = ['open', 'done', 'dropped'];
export const ACTION_STATUS_LABEL = {
  open: 'Open',
  done: 'Done',
  // "Dropped" rather than "cancelled": a decision not to do something is a
  // real outcome worth recording, and it reads differently from never having
  // been picked up.
  dropped: 'Dropped',
};

export const DEBRIEF_STATUS_LABEL = {
  draft: 'Draft',
  published: 'Published',
};

// A debrief is only useful once someone else can see it. Draft = still being
// written (staff can see it, actions aren't live); published = the actions
// count and it resurfaces on future projects.
export const isPublished = (debrief) => debrief?.status === 'published';

// Sort helper shared by topics and notes — explicit sort_order first, then
// creation, so a list that has never been reordered still reads chronologically.
export const bySortOrder = (a, b) =>
  (a?.sort_order ?? 0) - (b?.sort_order ?? 0)
  || String(a?.created_at || '').localeCompare(String(b?.created_at || ''));

// Assemble the flat rows into the nested shape the page renders. Notes whose
// topic has gone are dropped rather than orphaned into a phantom topic.
export function buildDebriefTree({ topics = [], notes = [], actions = [] } = {}) {
  const actionByNote = new Map((actions || []).filter(a => a?.note_id).map(a => [a.note_id, a]));
  const notesByTopic = new Map();
  for (const note of (notes || [])) {
    if (!note?.topic_id) continue;
    if (!notesByTopic.has(note.topic_id)) notesByTopic.set(note.topic_id, []);
    notesByTopic.get(note.topic_id).push({ ...note, action: actionByNote.get(note.id) || null });
  }
  return [...(topics || [])].sort(bySortOrder).map(topic => ({
    ...topic,
    notes: (notesByTopic.get(topic.id) || []).sort(bySortOrder),
  }));
}

// What the debrief adds up to — used for the list row and the header.
export function debriefSummary({ topics = [], notes = [], actions = [] } = {}) {
  const open = (actions || []).filter(a => a?.status === 'open').length;
  return {
    topics: (topics || []).length,
    notes: (notes || []).length,
    actions: (actions || []).length,
    openActions: open,
    // An empty debrief is worth distinguishing from one with nothing
    // outstanding — the first needs writing, the second is finished.
    empty: (notes || []).length === 0,
  };
}

// §5 RESURFACING — open actions from previous debriefs that plausibly apply to
// a project starting now.
//
// Option B costs us the client_id we'd have matched on, so matching is:
//   - the SAME CLIENT by name (normalised), when both sides have one, OR
//   - the same topic title, for actions that are about how we work rather than
//     about one client ("check the rigging brief covers first-time crew")
// Deliberately suggestive, never blocking, and anything dismissed on this
// project stays dismissed.
export function resurfacingCandidates(openActions, { projectRef, client, topicTitles = [] } = {}) {
  const clientKey = String(client ?? '').trim().toLowerCase();
  const topicKeys = new Set(topicTitles.map(t => String(t ?? '').trim().toLowerCase()).filter(Boolean));
  const refKey = projectRefKey(projectRef);

  return (openActions || []).filter(a => {
    if (!a || a.status !== 'open') return false;
    // Never resurface an action onto the project it came from.
    if (refKey && projectRefKey(a.project_ref) === refKey) return false;
    const dismissed = Array.isArray(a.dismissed_on_project_refs) ? a.dismissed_on_project_refs : [];
    if (dismissed.some(r => projectRefKey(r) === refKey)) return false;

    const sameClient = clientKey && String(a.client ?? '').trim().toLowerCase() === clientKey;
    const sameTopic = a.topic_title && topicKeys.has(String(a.topic_title).trim().toLowerCase());
    return !!(sameClient || sameTopic);
  });
}

// ============================================================================
// Cross-debrief actions list (§4)
// ============================================================================
// Actions are the part of a debrief that outlives it, so they get their own
// view across every debrief. Filtering is deliberately plain-object in/out so
// the page holds no filtering logic of its own.
//
// `actions` here are joined rows: the action plus its note body, topic title,
// and the debrief's project ref/date — see actionsWithContext below.
export function filterActions(actions = [], { owner = '', status = '', topic = '', search = '' } = {}) {
  const ownerKey = String(owner ?? '').trim().toLowerCase();
  const topicKey = String(topic ?? '').trim().toLowerCase();
  const q = String(search ?? '').trim().toLowerCase();
  return (actions || []).filter(a => {
    if (status && a.status !== status) return false;
    // An unowned action is a real state, not a missing filter value — "No owner"
    // is selectable and matches exactly those.
    if (ownerKey === '__none__') { if (String(a.owner ?? '').trim()) return false; }
    else if (ownerKey && String(a.owner ?? '').trim().toLowerCase() !== ownerKey) return false;
    if (topicKey && String(a.topic_title ?? '').trim().toLowerCase() !== topicKey) return false;
    if (q) {
      const hay = [a.note_body, a.owner, a.timeframe, a.topic_title, a.project_ref]
        .map(v => String(v ?? '').toLowerCase()).join(' ');
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

// The distinct values present, for the filter dropdowns. Only offering values
// that exist stops the filters promising combinations that return nothing.
export function actionFilterOptions(actions = []) {
  const owners = new Set();
  const topics = new Set();
  let unowned = false;
  for (const a of actions || []) {
    const o = String(a.owner ?? '').trim();
    if (o) owners.add(o); else unowned = true;
    const t = String(a.topic_title ?? '').trim();
    if (t) topics.add(t);
  }
  return {
    owners: [...owners].sort((a, b) => a.localeCompare(b)),
    topics: [...topics].sort((a, b) => a.localeCompare(b)),
    hasUnowned: unowned,
  };
}

// Flatten the four tables into one row per action carrying everything the list
// and the resurfacing matcher need. Keeps the joins in one place rather than
// each caller re-deriving topic_title from note→topic.
export function actionsWithContext({ debriefs = [], topics = [], notes = [], actions = [] } = {}) {
  const debriefById = new Map(debriefs.map(d => [d.id, d]));
  const topicById = new Map(topics.map(t => [t.id, t]));
  const noteById = new Map(notes.map(n => [n.id, n]));
  return (actions || []).map(a => {
    const note = noteById.get(a.note_id) || null;
    const topic = note ? topicById.get(note.topic_id) : null;
    const debrief = debriefById.get(a.debrief_id) || null;
    return {
      ...a,
      note_body: note?.body || '',
      topic_title: topic?.title || '',
      project_ref: debrief?.project_ref || '',
      project_name: debrief?.project_name || '',
      client: debrief?.client || '',
      debrief_date: debrief?.debrief_date || null,
    };
  });
}

// ============================================================================
// Export (§8)
// ============================================================================
// One self-contained HTML document: project header, topics as sections, notes
// as bullets, actions marked inline, and an ACTIONS SUMMARY at the end — the
// summary is the point of the export, so it's last where people look for it.
//
// HTML rather than a PDF library: Word opens it directly, browsers print it to
// PDF, it needs no dependency, and it stays readable if it's ever opened as a
// file. Everything is inlined so it survives being emailed on.
const esc = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// Note bodies are authored in a rich-text editor, so they arrive as HTML. They
// are staff-authored and already rendered as HTML in the app; the export keeps
// them as-is rather than stripping formatting the author chose.
const noteHtml = (body) => String(body ?? '');

export function debriefExportHtml({ debrief, tree = [], feedback = [], attachments = [], briefName = null } = {}) {
  const title = `${debrief?.project_ref || 'Project'} — debrief`;
  const actionRows = [];
  const sections = tree.map(topic => {
    const notesHtml = (topic.notes || []).map(note => {
      const acts = note.actions || [];
      for (const a of acts) {
        actionRows.push({ ...a, topic_title: topic.title, note_body: note.body });
      }
      const marks = acts.map(a => `<span class="act">Action${a.owner ? ` — ${esc(a.owner)}` : ''}${a.timeframe ? `, ${esc(a.timeframe)}` : ''} (${esc(ACTION_STATUS_LABEL[a.status] || a.status)})</span>`).join(' ');
      return `<li>${noteHtml(note.body)}${marks ? ` ${marks}` : ''}</li>`;
    }).join('\n');
    return `<section><h2>${esc(topic.title)}</h2><ul>${notesHtml || '<li class="muted">No notes.</li>'}</ul></section>`;
  }).join('\n');

  const summary = actionRows.length
    ? `<table><thead><tr><th>Action</th><th>Owner</th><th>When</th><th>Topic</th><th>Status</th></tr></thead><tbody>${
        actionRows.map(a => `<tr><td>${noteHtml(a.note_body)}</td><td>${esc(a.owner || '—')}</td><td>${esc(a.timeframe || '—')}</td><td>${esc(a.topic_title)}</td><td>${esc(ACTION_STATUS_LABEL[a.status] || a.status)}</td></tr>`).join('')
      }</tbody></table>`
    : '<p class="muted">No actions were raised.</p>';

  const feedbackHtml = feedback.length
    ? `<section><h2>Post-event feedback</h2><ul>${feedback.map(f => `<li><strong>${esc(f.supplier_name || 'Supplier')}</strong>${f.rating != null ? ` — ${esc(f.rating)}/5` : ''}${f.reportable ? ' <span class="act">Reportable incident</span>' : ''}${f.comments ? `<br>${esc(f.comments)}` : ''}</li>`).join('')}</ul></section>`
    : '';

  // Attachments are LISTED, not embedded — the export is a single HTML file
  // people mail around, and inlining a 20MB photo makes it unmailable. The
  // links point at storage, so they only work for someone with access; that's
  // correct for an internal document, but the export must say so rather than
  // leaving a colleague clicking a dead link and assuming it's broken.
  const attachmentsHtml = attachments.length
    ? `<section><h2>Attachments</h2><ul>${
        attachments.map(a => `<li><a href="${esc(a.file_url || '')}">${esc(a.file_name || 'File')}</a>${
          a.uploaded_by_user?.full_name ? ` <span class="muted">— ${esc(a.uploaded_by_user.full_name)}</span>` : ''
        }</li>`).join('')
      }</ul><p class="muted">Links open the file in Supply Pilot's storage — they need a Fusion 2K login.</p></section>`
    : '';

  return `<!doctype html>
<html><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>
  body { font-family: Calibri, Helvetica, Arial, sans-serif; color:#131313; line-height:1.5; max-width:800px; margin:32px auto; padding:0 24px; }
  h1 { font-size:22px; margin:0 0 4px; }
  h2 { font-size:15px; text-transform:uppercase; letter-spacing:0.08em; color:#004733; margin:28px 0 8px; border-bottom:1px solid #DDE1D0; padding-bottom:4px; }
  .meta { color:#51534A; font-size:13px; margin:0 0 24px; }
  ul { margin:0; padding-left:20px; } li { margin:0 0 6px; }
  .act { display:inline-block; font-size:11px; font-weight:700; background:#E9F3DC; color:#3B6D11; border:1px solid #C9E3AC; border-radius:10px; padding:1px 7px; }
  .muted { color:#8A8E7F; }
  table { border-collapse:collapse; width:100%; font-size:13px; margin-top:8px; }
  th, td { border:1px solid #DDE1D0; padding:6px 8px; text-align:left; vertical-align:top; }
  th { background:#F4F6EA; font-size:11px; text-transform:uppercase; letter-spacing:0.06em; }
  @media print { body { margin:0; } h2 { break-after:avoid; } table { break-inside:auto; } }
</style></head>
<body>
  <h1>${esc(briefName || debrief?.project_name || debrief?.project_ref || 'Project debrief')}</h1>
  <p class="meta">
    ${esc(debrief?.project_ref || '')}${debrief?.project_name ? ` · ${esc(debrief.project_name)}` : ''}${debrief?.client ? ` · ${esc(debrief.client)}` : ''}<br>
    ${debrief?.debrief_date ? `Debrief held ${esc(debrief.debrief_date)}` : 'Date not set'}
    ${debrief?.attendees ? `<br>Attendees: ${esc(debrief.attendees)}` : ''}
    <br>Status: ${esc(DEBRIEF_STATUS_LABEL[debrief?.status] || debrief?.status || 'draft')}
  </p>
  ${sections || '<p class="muted">No topics yet.</p>'}
  ${feedbackHtml}
  ${attachmentsHtml}
  <section><h2>Actions summary</h2>${summary}</section>
</body></html>`;
}
